import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Plus, Pencil, Trash2, Star, StarOff, Eye, EyeOff, Archive, ArchiveRestore,
  ExternalLink, X, LogOut, LayoutTemplate, Braces, Wrench, BookOpen, File, Workflow, Cloud,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { adminContent } from "@/admin/adminContent";
import { mediaUrl } from "@/lib/mediaUrl";
import { adminFetch, adminUpload, getToken, clearToken } from "@/admin/api";

const TYPE_ICONS = {
  tool: Wrench, template: LayoutTemplate, prompt: Braces,
  guide: BookOpen, file: File, automation: Workflow, saas_resource: Cloud,
};

const EMPTY_FORM = {
  title_ar: "", title_en: "", desc_ar: "", desc_en: "",
  thumbnail: "", external_url: "",
  platform: "website", type: "tool", access: "free",
  published: false, featured: false, archived: false,
};

const toPayload = (r) => ({
  title_ar: r.title_ar, title_en: r.title_en,
  desc_ar: r.desc_ar || "", desc_en: r.desc_en || "",
  thumbnail: r.thumbnail || "", external_url: r.external_url || "",
  platform: r.platform, type: r.type, access: r.access,
  published: !!r.published, featured: !!r.featured, archived: !!r.archived,
});

const inputCls =
  "h-11 w-full rounded-xl border border-line bg-softwhite px-4 text-sm text-ink outline-none transition-colors focus:border-sage";
const labelCls = "mb-1.5 block text-xs font-semibold text-graphite";

const AdminResources = () => {
  const { lang, toggle } = useLanguage();
  const t = adminContent[lang];
  const navigate = useNavigate();
  const [resources, setResources] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await adminFetch("/admin/resources");
      setResources(data.resources || []);
    } catch (err) {
      if (err.message !== "Unauthorized") {
        toast.error(err.message);
        setResources([]);
      }
    }
  }, []);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login", { replace: true });
      return;
    }
    load();
  }, [load, navigate]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setModal({ mode: "create" });
  };

  const openEdit = (r) => {
    setForm(toPayload(r));
    setModal({ mode: "edit", resource: r });
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal.mode === "create") {
        await adminFetch("/admin/resources", { method: "POST", body: JSON.stringify(form) });
      } else {
        await adminFetch(`/admin/resources/${modal.resource.id}`, { method: "PUT", body: JSON.stringify(form) });
      }
      toast.success(t.toasts.saved);
      setModal(null);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const patch = async (r, changes) => {
    try {
      await adminFetch(`/admin/resources/${r.id}`, {
        method: "PUT",
        body: JSON.stringify({ ...toPayload(r), ...changes }),
      });
      toast.success(t.toasts.updated);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(t.actions.confirmDelete)) return;
    try {
      await adminFetch(`/admin/resources/${r.id}`, { method: "DELETE" });
      toast.success(t.toasts.deleted);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error(t.toasts.tooLarge);
      return;
    }
    setUploading(true);
    try {
      const uploaded = await adminUpload(file);
      setForm((f) => ({ ...f, thumbnail: uploaded.url }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const logout = () => {
    clearToken();
    navigate("/admin/login", { replace: true });
  };

  const actionBtn = "flex size-9 items-center justify-center rounded-full border border-line bg-white text-graphite/70 transition-colors hover:border-sage hover:text-sage-deep";

  return (
    <div className="min-h-screen bg-softwhite" data-testid="admin-dashboard">
      <Toaster position="bottom-center" />
      <header className="sticky top-0 z-40 border-b border-line bg-softwhite/85 backdrop-blur-xl">
        <div className="mx-auto flex min-h-16 max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-3">
          <div className="flex items-center gap-4">
          <img src={mediaUrl("/brand/logo-primary.png")} alt="YacoubCreates" className="h-6 w-auto" width="374" height="93" />
            <span className="hidden text-sm font-semibold text-graphite/60 sm:block">{t.dashboard.title}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a href="/admin/saas-explainers" className="flex h-9 items-center rounded-full border border-line bg-white px-4 text-xs font-semibold text-graphite transition-colors hover:border-sage hover:text-sage-deep">
              SaaS Explainers
            </a>
            <a href="/admin/projects" className="hidden h-9 items-center rounded-full border border-line bg-white px-4 text-xs font-semibold text-graphite transition-colors hover:border-sage hover:text-sage-deep sm:flex">
              {({ ar: "المشاريع", en: "Projects", fr: "Projets" })[lang]}
            </a>
            <LanguageSwitcher />
            <a href="/" data-testid="admin-view-site" className="hidden h-9 items-center rounded-full border border-line bg-white px-4 text-xs font-semibold text-graphite transition-colors hover:border-sage hover:text-sage-deep sm:flex">
              {t.dashboard.viewSite}
            </a>
            <button type="button" onClick={logout} data-testid="admin-logout-button" className="flex h-9 items-center gap-1.5 rounded-full border border-line bg-white px-4 text-xs font-semibold text-graphite transition-colors hover:border-red-300 hover:text-red-600">
              <LogOut className="size-3.5" />
              {t.dashboard.logout}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-charcoal">{t.dashboard.title}</h1>
            <p className="mt-1.5 text-sm text-graphite/60">{t.dashboard.subtitle}</p>
          </div>
          <button type="button" onClick={openCreate} data-testid="resource-new-button" className="flex h-11 items-center gap-2 rounded-full bg-charcoal px-6 text-sm font-semibold text-softwhite transition-colors hover:bg-graphite">
            <Plus className="size-4" />
            {t.dashboard.newResource}
          </button>
        </div>

        <div className="mt-8 flex flex-col gap-4">
          {resources === null && <p className="py-16 text-center text-sm text-graphite/50">{t.dashboard.loading}</p>}
          {resources !== null && resources.length === 0 && (
            <p data-testid="admin-empty-state" className="rounded-3xl border border-dashed border-line bg-white py-16 text-center text-sm text-graphite/50">
              {t.dashboard.empty}
            </p>
          )}
          {(resources || []).map((r, i) => {
            const Icon = TYPE_ICONS[r.type] || Wrench;
            return (
              <div key={r.id} data-testid={`admin-resource-card-${i}`} className="flex flex-col gap-4 rounded-3xl border border-line bg-white p-4 shadow-card sm:flex-row sm:items-center">
                {r.thumbnail ? (
                  <img src={r.thumbnail} alt="" className="h-20 w-32 shrink-0 rounded-2xl object-cover" />
                ) : (
                  <div className="bg-dots flex h-20 w-32 shrink-0 items-center justify-center rounded-2xl bg-mist">
                    <Icon className="size-6 text-sage-deep" strokeWidth={1.6} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <h2 className="text-base font-bold text-charcoal">{lang === "ar" ? r.title_ar : r.title_en}</h2>
                    <span className="text-xs text-graphite/45">{lang === "ar" ? r.title_en : r.title_ar}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="rounded-full bg-mist px-2.5 py-0.5 text-[11px] font-semibold text-graphite">{t.types[r.type] || r.type}</span>
                    <span className="rounded-full border border-line px-2.5 py-0.5 text-[11px] font-medium text-graphite/60">{t.access[r.access] || r.access}</span>
                    <span className="rounded-full border border-line px-2.5 py-0.5 text-[11px] font-medium text-graphite/60">{t.platforms[r.platform] || r.platform}</span>
                    <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${r.published ? "bg-sage/15 text-sage-deep" : "bg-graphite/5 text-graphite/50"}`}>
                      {r.published ? t.status.published : t.status.draft}
                    </span>
                    {r.featured && <span className="rounded-full bg-sage px-2.5 py-0.5 text-[11px] font-semibold text-white">{t.status.featured}</span>}
                    {r.archived && <span className="rounded-full bg-charcoal/80 px-2.5 py-0.5 text-[11px] font-semibold text-softwhite">{t.status.archived}</span>}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {r.external_url && (
                    <a href={r.external_url} target="_blank" rel="noopener noreferrer" title={t.actions.open} data-testid={`resource-open-link-${i}`} className={actionBtn}>
                      <ExternalLink className="size-4 rtl:-scale-x-100" />
                    </a>
                  )}
                  <button type="button" onClick={() => patch(r, { published: !r.published })} title={r.published ? t.actions.unpublish : t.actions.publish} aria-label={r.published ? t.actions.unpublish : t.actions.publish} data-testid={`resource-publish-toggle-${i}`} className="flex h-9 items-center gap-1.5 rounded-full border border-line bg-white px-3 text-xs font-semibold text-graphite transition-colors hover:border-sage hover:text-sage-deep">
                    {r.published ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    {r.published ? t.actions.unpublish : t.actions.publish}
                  </button>
                  <button type="button" onClick={() => patch(r, { featured: !r.featured })} title={r.featured ? t.actions.unfeature : t.actions.feature} data-testid={`resource-feature-toggle-${i}`} className={actionBtn}>
                    {r.featured ? <StarOff className="size-4" /> : <Star className="size-4" />}
                  </button>
                  <button type="button" onClick={() => patch(r, { archived: !r.archived })} title={r.archived ? t.actions.unarchive : t.actions.archive} data-testid={`resource-archive-toggle-${i}`} className={actionBtn}>
                    {r.archived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}
                  </button>
                  <button type="button" onClick={() => openEdit(r)} title={t.actions.edit} data-testid={`resource-edit-${i}`} className={actionBtn}>
                    <Pencil className="size-4" />
                  </button>
                  <button type="button" onClick={() => remove(r)} title={t.actions.delete} aria-label={t.actions.delete} data-testid={`resource-delete-${i}`} className="flex h-9 items-center gap-1.5 rounded-full border border-line bg-white px-3 text-xs font-semibold text-red-600 transition-colors hover:border-red-300 hover:text-red-700">
                    <Trash2 className="size-4" />
                    {t.actions.delete}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      <AnimatePresence>
        {modal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/50 p-4 backdrop-blur-sm"
            onClick={() => setModal(null)}
            data-testid="resource-form-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-softwhite p-6 shadow-soft md:p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-bold text-charcoal">
                  {modal.mode === "create" ? t.form.createTitle : t.form.editTitle}
                </h2>
                <button type="button" onClick={() => setModal(null)} data-testid="resource-form-close" className="flex size-9 items-center justify-center rounded-full border border-line bg-white text-graphite transition-colors hover:border-sage">
                  <X className="size-4" />
                </button>
              </div>

              <form onSubmit={onSave} className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="resource-form">
                <div>
                  <label className={labelCls} htmlFor="f-title-ar">{t.form.titleAr}</label>
                  <input id="f-title-ar" dir="rtl" required value={form.title_ar} onChange={(e) => setForm({ ...form, title_ar: e.target.value })} data-testid="resource-form-title-ar" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="f-title-en">{t.form.titleEn}</label>
                  <input id="f-title-en" dir="ltr" required value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} data-testid="resource-form-title-en" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="f-desc-ar">{t.form.descAr}</label>
                  <textarea id="f-desc-ar" dir="rtl" rows={3} value={form.desc_ar} onChange={(e) => setForm({ ...form, desc_ar: e.target.value })} data-testid="resource-form-desc-ar" className="w-full rounded-xl border border-line bg-softwhite px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-sage" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="f-desc-en">{t.form.descEn}</label>
                  <textarea id="f-desc-en" dir="ltr" rows={3} value={form.desc_en} onChange={(e) => setForm({ ...form, desc_en: e.target.value })} data-testid="resource-form-desc-en" className="w-full rounded-xl border border-line bg-softwhite px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-sage" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="f-thumb">{t.form.thumbnailUrl}</label>
                  <div className="flex flex-wrap items-center gap-3">
                    <input id="f-thumb" dir="ltr" type="url" placeholder="https://" value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} data-testid="resource-form-thumbnail-url" className={`${inputCls} min-w-0 flex-1`} />
                    <label className="flex h-11 cursor-pointer items-center rounded-xl border border-dashed border-sage/60 bg-white px-4 text-xs font-semibold text-sage-deep transition-colors hover:bg-mist/50" data-testid="resource-form-thumbnail-upload">
                      {t.form.thumbnailUpload}
                      <input type="file" accept="image/*" className="hidden" onChange={onFile} />
                    </label>
                    {form.thumbnail && <img src={form.thumbnail} alt="" className="h-11 w-16 rounded-lg object-cover" data-testid="resource-form-thumbnail-preview" />}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="f-url">{t.form.externalUrl}</label>
                  <input id="f-url" dir="ltr" type="url" placeholder="https://" value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} data-testid="resource-form-external-url" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="f-platform">{t.form.platform}</label>
                  <select id="f-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} data-testid="resource-form-platform" className={inputCls}>
                    {Object.keys(t.platforms).map((k) => <option key={k} value={k}>{t.platforms[k]}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="f-type">{t.form.type}</label>
                  <select id="f-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="resource-form-type" className={inputCls}>
                    {Object.keys(t.types).map((k) => <option key={k} value={k}>{t.types[k]}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="f-access">{t.form.access}</label>
                  <select id="f-access" value={form.access} onChange={(e) => setForm({ ...form, access: e.target.value })} data-testid="resource-form-access" className={inputCls}>
                    {Object.keys(t.access).map((k) => <option key={k} value={k}>{t.access[k]}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-6 pt-6">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-graphite">
                    <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="resource-form-published" className="size-4 accent-sage" />
                    {t.form.published}
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-graphite">
                    <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="resource-form-featured" className="size-4 accent-sage" />
                    {t.form.featured}
                  </label>
                </div>
                <div className="mt-2 flex items-center justify-end gap-3 sm:col-span-2">
                  <button type="button" onClick={() => setModal(null)} data-testid="resource-form-cancel" className="h-11 rounded-full border border-line bg-white px-6 text-sm font-semibold text-graphite transition-colors hover:border-sage">
                    {t.form.cancel}
                  </button>
                  <button type="submit" disabled={saving || uploading} data-testid="resource-form-submit" className="h-11 rounded-full bg-charcoal px-8 text-sm font-semibold text-softwhite transition-colors hover:bg-graphite disabled:opacity-60">
                    {saving ? t.form.saving : t.form.save}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminResources;
